import { RISK_MARKER_COLORS } from "@/lib/leaflet-setup";

const CHART_HEIGHT = 220;

export interface RiskShareRow {
  label: string;
  /** Assets at risk 3 or 4. */
  hi: number;
  /** Assets with any computed risk (the denominator). */
  tot: number;
}

interface RiskShareBarListProps {
  rows: RiskShareRow[];
  emptyMessage: string;
}

/** Matches client/src/pages/admin/AdminStats.tsx's existing EmptyChartState pattern. */
function EmptyChartState({ message }: { message: string }) {
  return (
    <div className="flex items-center justify-center" style={{ height: CHART_HEIGHT }}>
      <div className="flex h-24 w-24 items-center justify-center rounded-full border-2 border-slate-200">
        <p className="px-3 text-center text-xs text-slate-400">{message}</p>
      </div>
    </div>
  );
}

/** Bands the bar's fill color by share-at-risk, reusing this app's existing
 * solid-fill Level-color scale (RISK_MARKER_COLORS, same as the map pins) —
 * a UI intensity choice for this chart, not a scoring decision. */
export function colorForShare(pct: number): string {
  if (pct >= 75) return RISK_MARKER_COLORS[4];
  if (pct >= 50) return RISK_MARKER_COLORS[3];
  if (pct >= 25) return RISK_MARKER_COLORS[2];
  return RISK_MARKER_COLORS[1];
}

/** A labeled horizontal "share at risk" bar per row (hazard, value chain
 * stage, or any other grouping), sorted worst-first. Shared by
 * HazardRiskBarChart and ValueChainRiskPanel so both present this metric
 * identically instead of each re-implementing the same bar/label markup. */
export function RiskShareBarList({ rows, emptyMessage }: RiskShareBarListProps) {
  const withData = rows.filter((r) => r.tot > 0);
  if (withData.length === 0) return <EmptyChartState message={emptyMessage} />;

  const sorted = [...withData].sort((a, b) => b.hi / b.tot - a.hi / a.tot);

  return (
    <div className="flex flex-col gap-3">
      {sorted.map((r) => {
        const pct = Math.round((100 * r.hi) / r.tot);
        return (
          <div key={r.label} className="flex items-center gap-3 text-xs">
            <span className="w-32 shrink-0 truncate font-medium" title={r.label}>
              {r.label}
            </span>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: colorForShare(pct) }} />
            </div>
            <span className="w-36 shrink-0 text-right text-muted-foreground">
              {r.hi} of {r.tot} assets at 3 or 4
            </span>
          </div>
        );
      })}
    </div>
  );
}
